import moment from 'moment';
import { removeMarkdownSyntax } from 'helpers/HelperFunctions';

// Skill experience is stored 0-100. Thresholds match the live experience bars.
export const SKILL_LEVELS = [
  { min: 66, label: 'Expert', ring: '#2CAE76' },
  { min: 33, label: 'Intermediate', ring: '#0777DA' },
  { min: 1, label: 'Beginner', ring: '#9AA5B2' },
];

export const LANGUAGE_LEVELS = [
  { min: 91, label: 'Fluent' },
  { min: 61, label: 'Advanced' },
  { min: 31, label: 'Intermediate' },
  { min: 1, label: 'Beginner' },
];

const findLevel = (levels, value) =>
  levels.find((level) => (value || 0) >= level.min) || null;

export const getSkillLevel = (experience) =>
  findLevel(SKILL_LEVELS, experience);

export const getLanguageLevel = (experience) =>
  findLevel(LANGUAGE_LEVELS, experience);

// Opt-in flag for non-specialised jobs; never shown to clients.
export const FREELANCE_GIG_SKILL = 'freelance gig';

export const isFreelanceGigSkill = (skill) =>
  !!skill &&
  ((skill.name || '').toLowerCase() === FREELANCE_GIG_SKILL ||
    (skill.slug || '').toLowerCase() === 'freelance-gig');

export const sortSkillsByLevel = (skills = []) =>
  [...skills].sort((a, b) => (b.experience || 0) - (a.experience || 0));

export const getEducationEntries = (education = []) =>
  education.filter((item) => item.organization);

export const getQualificationEntries = (education = []) =>
  education.filter((item) => item.qualification && !item.organization);

/**
 * Pass the credits API total once credits have loaded: it counts only the
 * projects the portfolio lists. The user stats also count inactive and child
 * credits, so they are only a fallback.
 */
export const hasProjects = (user, projectCount) => {
  if (typeof projectCount === 'number') {
    return projectCount > 0;
  }

  return (
    !!user &&
    ((user.stats?.num_of_projects || 0) > 0 ||
      (user.stats?.num_of_credits || 0) > 0)
  );
};

export const hasTestimonials = (user) =>
  !!user && (user.stats?.num_of_testimonials || 0) > 0;

export const countCareerEntries = (user) =>
  (user?.workexperience || []).length +
  getEducationEntries(user?.education).length +
  getQualificationEntries(user?.education).length;

/**
 * Two columns only when the wide column has real evidence to hold: projects,
 * testimonials, or at least three work, education and qualification entries.
 * Worked out from the user payload so the layout is fixed on first render.
 */
export const usesTwoColumns = (user, projectCount) =>
  hasProjects(user, projectCount) ||
  hasTestimonials(user) ||
  countCareerEntries(user) >= 3;

const HONORIFICS = ['mr', 'mrs', 'ms', 'miss', 'mx', 'dr', 'prof', 'sir'];

export const getFirstName = (displayname = '') => {
  const words = displayname.trim().split(/\s+/).filter(Boolean);
  const isHonorific = (word) =>
    HONORIFICS.includes(word.toLowerCase().replace(/\.$/, ''));
  const first =
    (words.length > 1 && isHonorific(words[0]) ? words[1] : words[0]) || '';
  return first.length > 0 && first.length <= 15 ? first : 'freelancer';
};

export const getInitials = (displayname = '') =>
  displayname
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0].toUpperCase())
    .join('');

/** The first sentence or two of the bio, as plain text, for the header. */
export const getBioSnippet = (bio = '', maxLength = 220) => {
  const text = removeMarkdownSyntax(bio || '')
    .replace(/\s+/g, ' ')
    .trim();

  if (!text) {
    return '';
  }

  const sentences = text.match(/[^.!?]+[.!?]+(\s|$)/g) || [text];
  let snippet = sentences[0].trim();

  if (sentences[1] && (snippet + ' ' + sentences[1]).length <= maxLength) {
    snippet = `${snippet} ${sentences[1].trim()}`;
  }

  if (snippet.length > maxLength) {
    snippet = `${snippet.slice(0, maxLength - 1).replace(/\s+\S*$/, '')}…`;
  }

  return snippet;
};

/** "1 yr 8 mos", "5 mos", "1 mo". Counts the end month, as LinkedIn does. */
export const formatDuration = (start, end) => {
  const startDate = moment(start);
  const endDate = end ? moment(end) : moment();

  if (!startDate.isValid() || !endDate.isValid() || endDate < startDate) {
    return '';
  }

  const months = endDate.diff(startDate.clone().startOf('month'), 'months') + 1;
  const years = Math.floor(months / 12);
  const rest = months % 12;
  const parts = [];

  if (years > 0) {
    parts.push(`${years} ${years === 1 ? 'yr' : 'yrs'}`);
  }
  if (rest > 0) {
    parts.push(`${rest} ${rest === 1 ? 'mo' : 'mos'}`);
  }

  return parts.join(' ');
};

/** "Mar 2025 – Present · 1 yr 8 mos" */
export const formatWorkDates = (startDate, endDate) => {
  const end = endDate ? moment(endDate) : null;
  const endLabel = end && end.isValid() ? end.format('MMM YYYY') : 'Present';

  if (!startDate || !moment(startDate).isValid()) {
    return endLabel;
  }

  const range = `${moment(startDate).format('MMM YYYY')} – ${endLabel}`;
  const duration = formatDuration(startDate, endDate);

  return duration ? `${range} · ${duration}` : range;
};

/** "2011 – 2014", or just the end year. */
export const formatYearRange = (startDate, endDate) => {
  const end =
    endDate && moment(endDate).isValid() ? moment(endDate).format('YYYY') : '';
  const start =
    startDate && moment(startDate).isValid()
      ? moment(startDate).format('YYYY')
      : '';

  if (start && end && start !== end) {
    return `${start} – ${end}`;
  }

  return end || start;
};

/**
 * Whole years across the work history. Overlapping roles are merged so time
 * isn't counted twice, and a role with no end date runs to today.
 */
export const getYearsOfExperience = (workexperience = [], now = moment()) => {
  const ranges = (workexperience || [])
    .map((item) => [
      moment(item.start_date),
      item.end_date
        ? moment.min(moment(item.end_date), moment(now))
        : moment(now),
    ])
    .filter(([start, end]) => start.isValid() && end.isValid() && end >= start)
    .sort((a, b) => a[0] - b[0]);

  let months = 0;
  let current = null;

  ranges.forEach(([start, end]) => {
    if (current && start <= current[1]) {
      if (end > current[1]) {
        current[1] = end;
      }
      return;
    }
    if (current) {
      months += current[1].diff(current[0], 'months', true);
    }
    current = [start, end];
  });

  if (current) {
    months += current[1].diff(current[0], 'months', true);
  }

  return Math.floor(months / 12);
};

/** "11 years' experience", "1 year's experience", or '' under a year. */
export const formatYearsOfExperience = (years) => {
  if (!years || years < 1) {
    return '';
  }

  return years === 1 ? "1 year's experience" : `${years} years' experience`;
};

/** "Joined March 2021", or '' when the date is missing. */
export const formatJoined = (joined) =>
  joined && moment(joined).isValid()
    ? `Joined ${moment(joined).format('MMMM YYYY')}`
    : '';

// Owners add this to their portfolio URL to see it as a client does.
export const CLIENT_PREVIEW_SEARCH = '?view=client';

export const isClientPreview = (search = '') =>
  new URLSearchParams(search || '').get('view') === 'client';
